import { apiPost } from "@/shared/api";
import { orderHasAllServiceSku } from "./allserviceApi";
import { FORNITORI_CON_PORTALE } from "./portalOrdersApi";
import type { Order } from "./types";

// «Riordina» (Davide 2026-09-24): lo stesso ordine al fornitore, di nuovo, per
// il pezzo arrivato rotto — numero «S-<ref>», poi «S2-», «S3-»… Un tasto solo
// per tutti i fornitori a cui OM sa ordinare; il backend smista sul motore.

export type FornitoreRiordino = { id: string; nome: string };

const NOMI: Record<string, string> = {
  allservice: "AllService",
  cosma: "Cosma",
  ityhome: "Ityhome",
  itamoby: "Itamoby",
  bizzotto: "Bizzotto",
  webfurniture: "Web Furniture",
  aurora: "Aurora Bedding",
  mauroferretti: "Mauro Ferretti",
};

/** A chi si rifa' l'ordine, o `null` se il tasto non ha senso.
 *
 *  Stesso smistamento del tasto «Ordina al fornitore» (AllService vince su
 *  tutto, poi Cosma, poi portale, poi mail), e solo con l'ordine originale gia'
 *  partito (`supplierSendState === "sent"`): una sostituzione di qualcosa mai
 *  ordinato e' un primo ordine, e ha il suo tasto. */
export function fornitoreRiordinoSost(
  order: Pick<
    Order,
    | "supplierSendState"
    | "sku"
    | "allserviceEligible"
    | "fornitore"
    | "portaleFornitore"
    | "fornitoreMail"
    | "wfEligible"
  >,
): FornitoreRiordino | null {
  if (order.supplierSendState !== "sent") return null;
  const id = orderHasAllServiceSku(order)
    ? "allservice"
    : order.fornitore.kind === "cosma"
      ? "cosma"
      : FORNITORI_CON_PORTALE.includes(order.portaleFornitore ?? "")
        ? (order.portaleFornitore ?? "")
        : (order.fornitoreMail ?? (order.wfEligible ? "webfurniture" : ""));
  const nome = NOMI[id];
  return nome ? { id, nome } : null;
}

export type AnteprimaSost = {
  fornitore: string;
  nome: string;
  /** Il numero che vedra' il fornitore: `S-<ref>`, poi `S2-<ref>`… */
  riferimento: string;
  /** Sostituzioni gia' fatte a questo fornitore per l'ordine. */
  precedenti: number;
  righe: { sku: string; quantita: number; prezzo: number | null }[];
  /** Il fornitore accetta solo alcune righe/quantita' (oggi AllService). */
  scelta_righe?: boolean;
  /** Solo portali: totale IVA inclusa letto dal portale. */
  totale_ivato: number | null;
  /** Portali: il costo lo dice il portale, il popup non lo chiede. */
  costo_dal_portale: boolean;
  /** Chi spedisce con la nostra etichetta: quella della sostituzione. */
  etichetta: string | null;
  /** `pronta`: c'e' gia'. `da_creare`: OM la emette su SDA alla conferma.
   *  `null`: l'etichetta la mette il fornitore (portali, AllService). */
  etichetta_stato?: "pronta" | "da_creare" | null;
  /** La sostituzione risulta gia' ordinata: non ripartirebbe. */
  gia_inviato: boolean;
  avvisi: string[];
};

const path = (orderId: string, coda = ""): string =>
  `/api/suppliers/riordina-sost/${encodeURIComponent(orderId)}${coda}`;

export function anteprimaSost(orderId: string, fornitore: string): Promise<AnteprimaSost> {
  return apiPost<AnteprimaSost>(path(orderId, "/anteprima"), { fornitore });
}

/** `avviso`: l'ordine e' PARTITO, ma il costo non e' finito nel registro. */
export type EsitoSost = { riferimento: string; avviso?: string };

export function riordinaSost(
  orderId: string,
  data: {
    fornitore: string;
    what: string;
    costGross: number | null;
    /** SKU → quantita' da riordinare; assente = tutto l'ordine. */
    righe?: Record<string, number>;
  },
): Promise<EsitoSost> {
  return apiPost<EsitoSost>(path(orderId), {
    fornitore: data.fornitore,
    what: data.what,
    cost_gross: data.costGross,
    ...(data.righe ? { righe: data.righe } : {}),
  });
}
